import { Fragment, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Tooltip } from "@fluentui/react-components";
import { FluentBadge } from "../../../../../components/FluentUI/Fluent-Badge";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import { ExcelExportColumn, toDataURL } from "@progress/kendo-react-excel-export";
import dayjs from "../../../../../util/dayjs-config";
import EmptyState from "../../../../../assets/img/Errors/NoDealWithinDashboard.svg";
import EmptyStateStaffer from "../../../../../assets/img/Errors/empty.svg";
import EmptyStateNoDealsOnFilter from "../../../../../assets/img/Errors/NoDealsOnFilter.svg";
import EmptyStateClipboard from "../../../../../assets/img/Errors/emptyDataClipboard.svg";
import { orderBy, filterBy } from "@progress/kendo-data-query";
import EditableDecimalCell from "../../../dashboard-util/dashboard-telerik-helper/editable-decimal-cell";
import { normalizeDate, removeDuplicateHeaderRows } from "../../../dashboard-util/dashboard-telerik-helper/dashboard-telerik-helper";
import PropTypes from "prop-types";


export const isRowEdited = (original, edited) => {
  return (
    original.LastWeekHours !== edited.LastWeekHours ||
    original.WeekendHours !== edited.WeekendHours ||
    original.NextWeekHours !== edited.NextWeekHours
  );
};

export const isRowEditedStaffer = (original, edited) => {
  return (
    original.LastWeekHours !== edited.LastWeekHours ||
    original.LastWeekendHours !== edited.LastWeekendHours ||
    original.CurrentWeekHours !== edited.CurrentWeekHours
  );
};

export const getEditedRows = (data, originalData) =>
  data.filter((editedRow, index) => isRowEdited(originalData[index], editedRow));

export const getEditedRowsStaffer = (data, originalData) =>
  data.filter((editedRow, index) => isRowEditedStaffer(originalData[index], editedRow));

export const renderWhenData = (dataLength, renderFunction) => {
  return dataLength === 0 ? () => null : renderFunction;
};

export const renderCellContent = (dataLength, emptyStateContent, filledContent) => {
  return dataLength === 0 ? emptyStateContent : filledContent;
};

export const highlightMatch = (text, search) => {
  if (!search || !text) return text;

  const trimmedSearch = search.trim();
  if (!trimmedSearch) return text;

  const lowerText = text.toLowerCase();
  const lowerSearch = trimmedSearch.toLowerCase();
  const index = lowerText.indexOf(lowerSearch);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + trimmedSearch.length);
  const after = text.slice(index + trimmedSearch.length);

  return (
    <>
      {before}
      <strong>{match}</strong>
      {after}
    </>
  );
};

export function makeDecimalCellHelper(visibleLength) {
  return (fieldName, nullFlagName) =>
    function ConditionalCell(props) {
      const isEditable = props.dataItem[nullFlagName];
      return renderCellContent(
        visibleLength,
        null,
        isEditable ? (
          <EditableDecimalCell
            {...props}
            emptyAsNull
            disabled={props.dataItem.IsOverdue}
          />
        ) : (
          <td className="background-white k-table-td">
            {props.dataItem[fieldName]}
          </td>
        )
      );
    };
}

export function makeDecimalCellHelperStaffer(visibleLength, IsProjectMember, userId) {
  return () =>
    function ConditionalCell(props) {
      const isUserProjectOwner = (props.dataItem?.Members || []).some(
        (member) => (member.User?.UserId ?? member.UserId ?? member.User?.Id ?? member.Id) === userId && member.IsOwner
      );
      const isDisabled = props.dataItem.IsOverdue || !props.dataItem.IsProjectMember || !IsProjectMember || !isUserProjectOwner;
      let disableReason;
      if (isDisabled) {
        if (props.dataItem.IsOverdue) {
          disableReason = "overdue";
        } else if (!props.dataItem.IsProjectMember) {
          disableReason = "notProjectMember";
        } else if (!isUserProjectOwner) {
          disableReason = "notOwner";
        }
      }
      return renderCellContent(
        visibleLength,
        null,
        <EditableDecimalCell
          {...props}
          emptyAsNull
          disabled={isDisabled}
          disableReason={disableReason}
        />
      );
    };
}

export function makeDecimalAnalystCellHelperStaffer(visibleLength, userId) {
  return () =>
    function ConditionalCell(props) {
      const isUserProjectOwner = (props.dataItem?.Members || []).some(
        (member) => (member.User?.UserId ?? member.UserId ?? member.User?.Id ?? member.Id) === userId && member.IsOwner
      );
      const isDisabled = props.dataItem.IsOverdue || !props.dataItem.IsProjectMember || !isUserProjectOwner;
      let disableReason;
      if (isDisabled) {
        if (props.dataItem.IsOverdue) {
          disableReason = "overdue";
        } else if (!props.dataItem.IsProjectMember) {
          disableReason = "notProjectMember";
        } else if (!isUserProjectOwner) {
          disableReason = "notOwner";
        }
      }
      return renderCellContent(
        visibleLength,
        null,
        <EditableDecimalCell
          {...props}
          emptyAsNull
          disabled={isDisabled}
          disableReason={disableReason}
        />
      );
    };
}

export const getAccordionMemberRowRender = (filteredData, groupState, toggleGroup, colspan, color = "informative") => {
  return (trElement, props) => {
    if (props.dataItem?.isEmptyRow ?? false) return trElement;

    const groupName = props.dataItem.GroupName;
    const isFirstInGroup =
      props.dataIndex ===
      filteredData.findIndex((item) => item.GroupName === groupName);
    const isVisible = groupState[groupName];

    if (!isFirstInGroup && !isVisible) return null;
    if (!isFirstInGroup) return isVisible ? trElement : null;

    const activeCount = filteredData.filter(
      (item) => item.GroupName === groupName
    ).length;

    return (
      <Fragment key={`group-${groupName}-${props.dataIndex}`}>
        <tr
          className="custom-group-header"
          style={{ backgroundColor: "#fff", height: "48px", borderBottom: "1px solid #edebe9", cursor: "pointer", lineHeight: "140%" }}
          onClick={() => toggleGroup(groupName)}
        >
          <td colSpan={colspan} style={{ padding: "0 16px", verticalAlign: "middle", borderBottom: "1px solid #edebe9", borderTop: "1px solid #edebe9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FluentButton
                className="accordion-toggle-button"
                disabled={false}
                icon={
                  <FluentUiIcon
                    name={isVisible ? "ChevronUpRegular" : "ChevronDownRegular"}
                    size={14}
                    disabled={false}
                  />
                }
                appearance="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupName);
                }}
              />
              <span style={{ fontWeight: "600", fontSize: "14px", marginLeft: "-10px" }}>
                {groupName ? groupName + "s" : ""}
              </span>
              <FluentBadge
                appearance="tint"
                size="large"
                color={color}
                text={`${activeCount} Banker${activeCount > 1 ? "s" : ""}`}
              />
            </div>
          </td>
        </tr>
        {isVisible && trElement}
      </Fragment>
    );
  };
};

export const getAccordionRowRender = (filteredData, groupState, toggleGroup, colspan) => {
  return (trElement, props) => {
    if (props.dataItem.isEmptyRow) return trElement;

    const groupName = props.dataItem.GroupName;
    const isFirstInGroup =
      props.dataIndex ===
      filteredData.findIndex((item) => item.GroupName === groupName);
    const isVisible = groupState[groupName];

    if (!isFirstInGroup && !isVisible) return null;
    if (!isFirstInGroup) return isVisible ? trElement : null;

    const activeCount = filteredData.filter(
      (item) => item.GroupName === groupName
    ).length;
    return (
      <Fragment key={`group-${groupName}-${props.dataIndex}`}>
        <tr
          className="custom-group-header"
          style={{ backgroundColor: "#fff", height: "48px", borderBottom: "1px solid #edebe9", cursor: "pointer", lineHeight: "140%" }}
          onClick={() => toggleGroup(groupName)}
        >
          <td colSpan={colspan} style={{ padding: "0 16px", verticalAlign: "middle", borderBottom: "1px solid #edebe9", borderTop: "1px solid #edebe9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FluentButton
                className="accordion-toggle-button"
                disabled={false}
                icon={
                  <FluentUiIcon
                    name={isVisible ? "ChevronUpRegular" : "ChevronDownRegular"}
                    size={14}
                    disabled={false}
                  />
                }
                appearance="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupName);
                }}
              />
              <span style={{ fontWeight: "600", fontSize: "14px", marginLeft: "-10px" }}>
                {groupName}
              </span>

              <FluentBadge
                appearance="tint"
                size="large"
                color={groupName === "Live" ? "success" : "informative"}
                text={`${activeCount} Deal${activeCount > 1 ? "s" : ""}`}
              />
            </div>
          </td>
        </tr>
        {isVisible && trElement}
      </Fragment>
    );
  };
};

export const getSectorColor = (sector, sectorNames) => {
  const index = sectorNames?.indexOf(sector);
  const classnames = ["sector-badge-purple", "sector-badge-pink", "brand", "success", "severe", "sector-badge-yellow"];
  return classnames[index % classnames.length] || "";
}

export const getAccordionRowRenderForStafferDeals = (filteredData, groupState, toggleGroup, colspan, sectorWithNoData, sectorNames, filters) => {

  return (trElement, props) => {
    if (props.dataItem.isEmptyRow) return trElement;

    const groupName = props.dataItem.GroupName;
    const isFirstInGroup =
      props.dataIndex ===
      filteredData.findIndex((item) => item.GroupName === groupName);
    const isVisible = groupState[groupName] ?? true;
    const isLastRow = props.absoluteRowIndex === filteredData.length - 1 && filters == null;
    if (!isFirstInGroup && !isVisible && !isLastRow) return null;
    if (!isFirstInGroup && !isLastRow) return isVisible ? trElement : null;

    const activeCount = filteredData.filter(
      (item) => item.GroupName === groupName
    ).length;
    const averageSubtotal = filteredData.filter(
      (item) => item.GroupName === groupName
    ).reduce((sum, item) => sum + (item.CurrentWeekHours || 0), 0)?.toFixed(1);

    const emptySectorsToRender = isLastRow
      ? sectorWithNoData
      : [];

    return (
      <Fragment key={`group-${groupName}-${props.dataIndex}`}>
        {(!isLastRow || isFirstInGroup) && <tr
          className="custom-group-header"
          style={{ backgroundColor: "#fff", height: "48px", borderBottom: "1px solid #edebe9", cursor: "pointer", lineHeight: "140%" }}
          onClick={() => toggleGroup(groupName)}
        >
          <td colSpan={colspan} style={{ padding: "0 16px", verticalAlign: "middle", borderBottom: "1px solid #edebe9", borderTop: "1px solid #edebe9" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <FluentButton
                className="accordion-toggle-button"
                disabled={false}
                icon={
                  <FluentUiIcon
                    name={isVisible ? "ChevronUpRegular" : "ChevronDownRegular"}
                    size={14}
                    disabled={false}
                  />
                }
                appearance="transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(groupName);
                }}
              />
              <span style={{ fontWeight: "600", fontSize: "14px", marginLeft: "-10px" }}>
                {groupName}
              </span>

              <FluentBadge
                appearance="tint"
                size="large"
                className={getSectorColor(groupName, sectorNames)}
                color={getSectorColor(groupName, sectorNames)}
                text={`${activeCount} Deal${activeCount > 1 ? "s" : ""}`}
              />
              <FluentBadge
                appearance="tint"
                size="large"
                color={getSectorColor(groupName, sectorNames)}
                className={getSectorColor(groupName, sectorNames)}
                text={`${averageSubtotal} Hour${averageSubtotal !== 1 ? "s" : ""}`}
              />
            </div>
          </td>
        </tr>}
        {isVisible && trElement}
        {/* Render empty sectors at the end */}
        {emptySectorsToRender.map(emptySector => (
          <tr
            key={`empty-sector-${emptySector}`}
            className="custom-group-header"
            style={{ backgroundColor: "#fff", height: "48px", borderBottom: "1px solid #edebe9", cursor: "pointer", lineHeight: "140%", opacity: "0.7" }}
          >
            <td colSpan={colspan} style={{ padding: "0 16px", verticalAlign: "middle", borderBottom: "1px solid #edebe9", borderTop: "1px solid #edebe9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FluentButton
                  className="accordion-toggle-button"
                  disabled={true}
                  icon={
                    <FluentUiIcon
                      name="ChevronDownRegular"
                      size={14}
                      disabled={true}
                    />
                  }
                  appearance="transparent"
                />
                <span style={{ fontWeight: "600", fontSize: "14px", marginLeft: "-10px", color: "#6c757d" }}>
                  {emptySector}
                </span>
                <FluentBadge
                  appearance="tint"
                  size="large"
                  className={getSectorColor(emptySector, sectorNames)}
                  color={getSectorColor(emptySector, sectorNames)}
                  text={`0 Deals`}
                />
                <FluentBadge
                  appearance="tint"
                  size="large"
                  className={getSectorColor(emptySector, sectorNames)}
                  color={getSectorColor(emptySector, sectorNames)}
                  text={`0.0 Hours`}
                />
              </div>
            </td>
          </tr>
        ))}
      </Fragment>
    );
  };
};

export const applySearchFilter = (searchValue, detailState, onDetailStateChange, setGroupState) => {
  const trimmed = searchValue.trim();

  if (!trimmed) {
    onDetailStateChange({
      ...detailState,
      filter: null,
      skip: 0,
    });

    setGroupState({
      Live: true,
      "Non-Live": false,
    });

    return;
  }

  const searchFilters = [
    { field: "ProjectName", operator: "contains", value: trimmed },
    { field: "Company", operator: "contains", value: trimmed },
  ];

  onDetailStateChange({
    ...detailState,
    filter: {
      logic: "or",
      filters: searchFilters,
    },
    skip: 0,
  });

  setGroupState({
    Live: true,
    "Non-Live": true,
  });
};

export const getFlattenedData = (data) => {
  return data?.map((item) => {
    const memberNames = item.Members?.map((m) => m.User?.Name).filter(Boolean);
    const seniorDealMembersName = item?.SeniorDealMembers?.map((m) => m.User?.Name).filter(Boolean);
    return {
      ...item,
      MemberNamesArray: memberNames || [],
      MemberNamesString: memberNames?.join(", ") || "",
      SeniorDealMembersNamesArray: seniorDealMembersName || [],
    };
  });
};

export const getFilteredSortedData = (flattenedData, filter, sort) => {
  let temp = [...flattenedData];

  const memberFilters = [];

  (filter?.filters || []).forEach((f) => {
    if (f.field === "Members") {
      memberFilters.push(f.value?.toLowerCase());
    } else if (Array.isArray(f.filters)) {
      f.filters.forEach((inner) => {
        if (inner.field === "Members") {
          memberFilters.push(inner.value?.toLowerCase());
        }
      });
    }
  });

  if (memberFilters.length > 0) {
    temp = temp.filter((item) =>
      item.MemberNamesArray?.some((name) =>
        memberFilters.includes((name || "").toLowerCase())
      )
    );
  }

  const kendoCompatibleFilters =
    filter?.filters?.filter(
      (f) => f.field !== "Members" && !f.filters?.some((inner) => inner.field === "Members")
    ) ?? [];

  const processedFilter = { ...filter, filters: kendoCompatibleFilters };
  temp =
    processedFilter.filters.length > 0 ? filterBy(temp, processedFilter) : temp;

  const sorted = sort?.length
    ? sort[0].field === "Members"
      ? [...temp].sort((a, b) => {
        const countA = a.MemberNamesArray?.length || 0;
        const countB = b.MemberNamesArray?.length || 0;
        return sort[0].dir === "asc" ? countA - countB : countB - countA;
      })
      : orderBy(temp, sort)
    : temp;

  return sorted.sort((a, b) => {
    if (a.GroupName === b.GroupName) return 0;
    if (a.GroupName === "Live") return -1;
    if (b.GroupName === "Live") return 1;
    return 0;
  });
};

export const getFilteredSortedData2 = (flattenedData, filter, sort, priorityOrder) => {
  let temp = Array.isArray(flattenedData) ? [...flattenedData] : [];
  const memberFilters = [];

  (filter?.filters || []).forEach((f) => {
    if (f.field === "Members") {
      memberFilters.push(f.value?.toLowerCase());
    } else if (Array.isArray(f.filters)) {
      f.filters.forEach((inner) => {
        if (inner.field === "Members") {
          memberFilters.push(inner.value?.toLowerCase());
        }
      });
    }
  });

  if (memberFilters.length > 0) {
    temp = temp.filter((item) =>
      item.MemberNamesArray?.some((name) =>
        memberFilters.includes((name || "").toLowerCase())
      )
    );
  }

  const kendoCompatibleFilters =
    filter?.filters?.filter(
      (f) => f.field !== "Members" && !f.filters?.some((inner) => inner.field === "Members")
    ) ?? [];

  const processedFilter = { ...filter, filters: kendoCompatibleFilters };
  temp =
    processedFilter.filters.length > 0 ? filterBy(temp, processedFilter) : temp;

  const sortedData = Array.isArray(sort) && sort.length
    ? sort[0].field === "Members"
      ? [...temp].sort((a, b) => {
        const countA = a.MemberNamesArray?.length || 0;
        const countB = b.MemberNamesArray?.length || 0;
        return sort[0].dir === "asc" ? countA - countB : countB - countA;
      })
      : orderBy(temp, sort)
    : temp;

  const prioritized = (Array.isArray(sortedData) ? sortedData : []).sort((a, b) => {
    const aName = a.GroupName || "";
    const bName = b.GroupName || "";
    if (aName === bName) return 0;

    // Guard priorityOrder; treat missing as empty array
    const priorities = Array.isArray(priorityOrder) ? priorityOrder : [];
    const aIdx = priorities.indexOf(aName);
    const bIdx = priorities.indexOf(bName);
    const aIn = aIdx !== -1;
    const bIn = bIdx !== -1;

    if (aIn && !bIn) return -1;
    if (bIn && !aIn) return 1;
    if (aIn && bIn) return aIdx - bIdx;

    // Fallback alphabetical if neither is prioritized
    return aName.localeCompare(bName);
  });

  return prioritized;
};

export const getAnalystFilteredSortedData = (flattenedData, filter, sort) => {
  let temp = [...flattenedData];

  const memberFilters = [];

  (filter?.filters || []).forEach((f) => {
    if (f.field === "Members") {
      memberFilters.push(f.value?.toLowerCase());
    } else if (Array.isArray(f.filters)) {
      f.filters.forEach((inner) => {
        if (inner.field === "Members") {
          memberFilters.push(inner.value?.toLowerCase());
        }
      });
    }
  });

  if (memberFilters.length > 0) {
    temp = temp.filter((item) =>
      item.MemberNamesArray?.some((name) =>
        memberFilters.includes((name || "").toLowerCase())
      )
    );
  }

  const kendoCompatibleFilters =
    filter?.filters?.filter(
      (f) => f.field !== "Members" && !f.filters?.some((inner) => inner.field === "Members")
    ) ?? [];

  const processedFilter = { ...filter, filters: kendoCompatibleFilters };
  temp =
    processedFilter.filters.length > 0 ? filterBy(temp, processedFilter) : temp;

  const sorted = sort?.length
    ? sort[0].field === "Members"
      ? [...temp].sort((a, b) => {
        const countA = a.MemberNamesArray?.length || 0;
        const countB = b.MemberNamesArray?.length || 0;
        return sort[0].dir === "asc" ? countA - countB : countB - countA;
      })
      : orderBy(temp, sort)
    : temp;

  return sorted.sort((a, b) => {
    if (a.GroupName === b.GroupName) return 0;
    if (a.GroupName === "Analyst") return -1;
    if (b.GroupName === "Analyst") return 1;
    return 0;
  });
};

export const useMemberGroupToggle = (initialState = { Analyst: true, Associate: true }) => {
  const [groupState, setGroupState] = useState(initialState);

  const toggleGroup = useCallback((groupName) => {
    setGroupState((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  return { groupState, toggleGroup, setGroupState };
};

export const useGroupToggle = (initialState = { Live: true, "Non-Live": false }) => {
  const [groupState, setGroupState] = useState(initialState);

  const toggleGroup = useCallback((groupName) => {
    setGroupState((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  }, []);

  return { groupState, toggleGroup, setGroupState };
};

export const useGroupToggle2 = (initialState = { Upstream: true }) => {
  const [groupState, setGroupState] = useState(initialState);

  const toggleGroup = useCallback((groupName) => {
    setGroupState((prev) => ({
      ...prev,
      [groupName]: prev[groupName] !== undefined ? !prev[groupName] : false,
    }));
  }, []);

  return { groupState, toggleGroup, setGroupState };
};

// Submit Helpers
export const mergeUpdatedEnergyData = (updatedProjects) => {
  return (updatedProjects || []).map((deal, index) => {
    const initiallyNull_LastWeekHours = deal.LastWeekHours == null;
    const initiallyNull_WeekendHours = deal.WeekendHours == null;
    const initiallyNull_NextWeekHours = deal.NextWeekHours == null;

    return {
      ...deal,
      InitiallyNull_LastWeekHours: initiallyNull_LastWeekHours,
      InitiallyNull_WeekendHours: initiallyNull_WeekendHours,
      InitiallyNull_NextWeekHours: initiallyNull_NextWeekHours,
      inEdit: initiallyNull_LastWeekHours || initiallyNull_WeekendHours || initiallyNull_NextWeekHours,
    };
  });
};

export const buildEnergyPayload = (rows) =>
  rows.map(
    ({ ProjectId, TaskId, LastWeekHours, WeekendHours, NextWeekHours }) => ({
      ProjectId,
      TaskId,
      LastWeekHours,
      WeekendHours,
      NextWeekHours,
    })
  );

export const buildEnergyPayloadForStaffer = (rows, analystId) =>
  buildEnergyPayload(rows).map((payload) => ({
    ...payload,
    AnalystId: analystId,
  }));

// Export Data Helpers
export const mapEnergyExportData = (data) => {
  return data.map((item) => {
    const owners = item.Members?.filter((m) => m.IsOwner)
      .map((m) => m.User?.Name)
      .filter(Boolean)
      .join(", ");

    const members = item.Members?.filter((m) => !m.IsOwner && !m?.IsSeniorDealMember && !m?.IsStaffer)
      .map((m) => m.User?.Name)
      .filter(Boolean)
      .join(", ");

    const seniorDeal = item.Members?.filter((m) => m?.IsSeniorDealMember)
      .map((m) => m.User?.Name)
      .filter(Boolean)
      .join(", ");

    return {
      ...item,
      Owners: owners || "-",
      Members: members || "-",
      SeniorDealMembers: seniorDeal || "-",
    };
  });
};

//Resource Deal Drill down
export const mapEnergyDrillDownExportData = (data, company, projectName, projectId) => {
  return data.map((item) => {
    return {
      ...item,
      Company: company || "-",
      ProjectName: projectName || "-",
      ProjectId: projectId || "-",
    };
  });
};

export const getSummaryColumns = () => (
  <>
    <ExcelExportColumn field="User.Id" title="User ID" />
    <ExcelExportColumn field="User.Name" title="Name" />
    <ExcelExportColumn field="TotalHoursLastWeek" title="Total Wk Hrs Spent Last Week" />
    <ExcelExportColumn field="TotalWeekendHoursLastWeek" title="Total Wknd Hrs Spent Last Week" />
    <ExcelExportColumn field="TotalHoursNextWeek" title="Total Current Est. of Hrs (Nxt 7 Days)" />
  </>
);

export const getDetailColumns = () => (
  <>
    {/* <ExcelExportColumn field="ProjectId" title="ID" /> */}
    <ExcelExportColumn field="ProjectName" title="Deal Name" />
    <ExcelExportColumn field="Company" title="Company" />
    <ExcelExportColumn field="GroupName" title="Live/Non-Live" />
    <ExcelExportColumn field="LastWeekHours" title="Last Wk's Hrs (M-F)" />
    <ExcelExportColumn field="WeekendHours" title="Last Wknd Hrs (S-S)" />
    <ExcelExportColumn field="ActualTotalHours" title="Actual Total Hrs" />
    <ExcelExportColumn field="NextWeekHours" title="Current Est. of Hrs (Nxt 7 Days)" />
    {/* <ExcelExportColumn field="Owners" title="Owner" /> */}
    <ExcelExportColumn field="Members" title="Members" />
  </>
);

export const getAnalystsTimeLogColumns = () => (
  <>
    <ExcelExportColumn field="Name" title="Jr Banker" />
    <ExcelExportColumn field="TotalHoursLastWeek" title="Last Wk Hrs (M-F)" />
    <ExcelExportColumn field="TotalWeekendHoursLastWeek" title="Last Wknd Hrs (S-S)" />
    <ExcelExportColumn field="TotalActualHoursLastWeek" title="Actual Total Hrs (M-S)" />
    <ExcelExportColumn field="TotalPriorWeekEstimate" title="Prior Wk Est." />
    <ExcelExportColumn field="TotalHoursCurrentWeek" title="Current Wk Hrs" />
    <ExcelExportColumn field="PercentageHoursOnLiveDeals" title="% of Hrs on Live Deals" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" />
  </>
);

export const getAnalystTimeLogSummaryColumns = () => (
  <>
    <ExcelExportColumn field="Analyst.FullName" title="Analyst" />
    <ExcelExportColumn field="TotalHoursLastWeek" title="Total (Actual) Hrs Spent Last Week" />
    <ExcelExportColumn field="TotalHoursLiveDeals" title="Total (Actual) Hrs Spent on Live Deals" />
    <ExcelExportColumn field="TotalHoursNonLiveDeals" title="Total (Actual) Hrs Spent on Non-Live Deals" />
  </>
);

export const getAnalystTimeLogDetailColumns = () => (
  <>
    {/* <ExcelExportColumn field="ProjectId" title="ID" /> */}
    <ExcelExportColumn field="ProjectName" title="Deal Name" />
    <ExcelExportColumn field="Company" title="Company" />
    <ExcelExportColumn field="LastWeekHours" title="Last Wk Actual Hrs" />
    <ExcelExportColumn field="WeekendHours" title="Last Wknd Hrs" />
    <ExcelExportColumn field="ActualTotalHours" title="Actual Total Hrs" />
    <ExcelExportColumn field="PriorWeekEstimate" title="Prior Week Est" />
    <ExcelExportColumn field="NextWeekHours" title="Current Est. of Hrs" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" />
  </>
);

export const getResourceLogColumns = () => (
  <>
    {/* <ExcelExportColumn field="ProjectId" title="ID" /> */}
    <ExcelExportColumn field="ProjectName" title="Deal Name" />
    <ExcelExportColumn field="GroupName" title="Live/Non-Live" />
    <ExcelExportColumn field="Company" title="Company" />
    <ExcelExportColumn field="Sector" title="Sector" />
    <ExcelExportColumn field="CurrentWeekHours" title="Current Wk Hrs" />
    <ExcelExportColumn field="Description" title="Description" />
    {/* <ExcelExportColumn field="Owners" title="Owner" /> */}
    <ExcelExportColumn field="Members" title="Deal Team" />
  </>
);

export const getResourceLogColumnsGrouped = [
  // { field: "ProjectId", title: "ID" },
  { field: "ProjectName", title: "Deal Name" },
  { field: "Company", title: "Company" },
  { field: "Sector", title: "Sector" },
  { field: "Live", title: "Live/Non-Live" },
  { field: "Description", title: "Description" },
  // { field: "Owners", title: "Owner" },
  { field: "Members", title: "Deal Team" },
  { field: "SeniorDealMembers", title: "Senior Deal Team" },
  { field: "CurrentWeekHours", title: "Current Week Hrs" }
];

export const getJuniorBankersColumns = () => (
  <>
    {/* <ExcelExportColumn field="ProjectId" title="ID" /> */}
    <ExcelExportColumn field="ProjectName" title="Deal Name" />
    <ExcelExportColumn field="Company" title="Company" />
    <ExcelExportColumn field="Banker" title="Banker" />
    <ExcelExportColumn field="Level" title="Level" />
    <ExcelExportColumn field="LastWeekHours" title="Last Week Hrs" />
    <ExcelExportColumn field="LastWeekendHours" title="Last Weekend Hrs" />
    <ExcelExportColumn field="PriorWeekEstimate" title="Prior Week Estimate" />
    <ExcelExportColumn field="CurrentWeekHours" title="Current Week Hrs" />
    <ExcelExportColumn field="LastUpdated" title="Last Updated" />
  </>
);

export const getStaffingIndexColumns = () => (
  <>
    <ExcelExportColumn field="Date" title="Week of" />
    <ExcelExportColumn field="CurrentWkHrsTotal" title="Current Wk Hrs Total" />
    <ExcelExportColumn field="ReportedActualsTotal" title="Reported Actuals Total" />
    <ExcelExportColumn field="AvailableBankers" title="Available Bankers" />
    <ExcelExportColumn field="TotalBankers" title="Total Bankers" />
    <ExcelExportColumn field="EstimatedIndex" title="Estimated Index" />
    <ExcelExportColumn field="ReportedIndex" title="Reported Index" />
  </>
);

export const getEnergyTimeLogFileName = () => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  return `Banker Time Log ${timestamp}.xlsx`;
};

export const handleExport = async ({ summaryRef, detailRef }) => {
  if (!summaryRef.current || !detailRef.current) return;

  const fileName = getEnergyTimeLogFileName();

  const summaryWorkbook = summaryRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Summary";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = detailRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferAnalystsTimeLogExport = async ({ detailRef }) => {
  if (!detailRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Analyst & Associate Time Log ${timestamp}.xlsx`;

  const analystsWorkbook = detailRef.current.workbookOptions();
  if (analystsWorkbook?.sheets?.length > 0) {
    analystsWorkbook.sheets[0].title = "Analyst & Associate Time Log";
    removeDuplicateHeaderRows(analystsWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(analystsWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferAnalystTimeLogExport = async ({ summaryRef, detailRef }) => {
  if (!summaryRef.current || !detailRef.current) return;

  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Junior Banker Time Tracker ${timestamp}.xlsx`;

  const summaryWorkbook = summaryRef.current.workbookOptions();
  if (summaryWorkbook?.sheets?.length > 0) {
    summaryWorkbook.sheets[0].title = "Summary";
    removeDuplicateHeaderRows(summaryWorkbook.sheets[0]);
  }

  const detailWorkbook = detailRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Details";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(summaryWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



export const handleStafferDealExport = async ({ dealRef, juniorBankersRef }) => {
  if (!dealRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Deals and Resource Log ${timestamp}.xlsx`;

  const dealWorkbook = dealRef.current.workbookOptions();
  if (dealWorkbook?.sheets?.length > 0) {
    dealWorkbook.sheets[0].title = "Deals and Resource Log";
    removeDuplicateHeaderRows(dealWorkbook.sheets[0]);
  }

  const detailWorkbook = juniorBankersRef.current.workbookOptions();
  if (detailWorkbook?.sheets?.length > 0) {
    detailWorkbook.sheets[0].title = "Bankers";
    removeDuplicateHeaderRows(detailWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(dealWorkbook?.sheets || []),
      ...(detailWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleStafferDealJuniorBankerExport = async ({ jBRef, ProjectName, Company }) => {
  if (!jBRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `${ProjectName} - ${Company} Junior Bankers ${timestamp}.xlsx`;

  const dealWorkbook = jBRef.current.workbookOptions();
  if (dealWorkbook?.sheets?.length > 0) {
    dealWorkbook.sheets[0].title = "junior Bankers";
    removeDuplicateHeaderRows(dealWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(dealWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Local Storage Helpers
export const getStorageKey = (key, userId) => `${key}_user_${userId}`;

// Recursively walk any filter/group and normalize only the LastUpdatedString values
const normalizeLastUpdatedFilter = (filter) => {
  if (!filter || !Array.isArray(filter.filters)) {
    return filter;
  }

  return {
    ...filter,
    filters: filter.filters.map((f) => {
      // If this is a subgroup, recurse
      if (Array.isArray(f.filters)) {
        return normalizeLastUpdatedFilter(f);
      }

      // Leaf filter: only target LastUpdatedString
      if (f.field === "LastUpdatedString") {
        return {
          ...f,
          value: normalizeDate(f.value),  // now a Date or null
        };
      }

      // Everything else stays as-is
      return f;
    }),
  };
};

export const handleJuniorStaffingIndexExport = async ({ detailRef }) => {
  if (!detailRef.current) return;
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Junior Staffing Index ${timestamp}.xlsx`;

  const staffingIndexWorkbook = detailRef.current.workbookOptions();
  if (staffingIndexWorkbook?.sheets?.length > 0) {
    staffingIndexWorkbook.sheets[0].title = "Junior Staffing Index";
    removeDuplicateHeaderRows(staffingIndexWorkbook.sheets[0]);
  }

  const workbook = {
    sheets: [
      ...(staffingIndexWorkbook?.sheets || []),
    ],
  };

  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getInitialDetailState = (userId, baseKey = "energyBanker") => {
  if (!userId) return { sort: [], filter: null };

  try {
    const sortRaw = localStorage.getItem(getStorageKey(`${baseKey}Sort`, userId));
    const filterRaw = localStorage.getItem(getStorageKey(`${baseKey}Filter`, userId));
    const sort = sortRaw ? JSON.parse(sortRaw) : [];
    const filter = filterRaw
      ? normalizeLastUpdatedFilter(JSON.parse(filterRaw))
      : null;

    return { sort, filter };
  } catch (e) {
    console.warn("Failed to parse localStorage", e);
    return { sort: [], filter: null };
  }
};

export const getInitialDetailState1 = (userId, baseKey = "energyBanker") => {
  if (!userId) return { sort: [], filter: null };

  try {
    const sortRaw = localStorage.getItem(getStorageKey(`${baseKey}Sort`, userId));
    const filterRaw = localStorage.getItem(getStorageKey(`${baseKey}Filter`, userId));
    const sort = sortRaw ? JSON.parse(sortRaw) : [];
    const filter = filterRaw ? JSON.parse(filterRaw) : null;
    return { sort, filter };
  } catch (e) {
    console.warn("Failed to parse localStorage", e);
    return { sort: [], filter: null };
  }
};

export const saveDetailStateToStorage = (userId, newState, searchValue, baseKey = "energyBanker") => {
  if (!userId || searchValue?.trim()) return;
  localStorage.setItem(getStorageKey(`${baseKey}Sort`, userId), JSON.stringify(newState.sort));
  localStorage.setItem(getStorageKey(`${baseKey}Filter`, userId), JSON.stringify(newState.filter));
};

export const clearDetailStateFromStorage = (userId, baseKey = "energyBanker") => {
  if (!userId) return;
  localStorage.removeItem(getStorageKey(`${baseKey}Sort`, userId));
  localStorage.removeItem(getStorageKey(`${baseKey}Filter`, userId));
};

//Search Dialog Helper
export const getFilteredSortedDatDialog = (flattenedData, filter, sort) => {
  let temp = [...flattenedData];

  const memberFilters = [];
  (filter?.filters || []).forEach(f => {
    if (f.field === "Members") {
      memberFilters.push(f.value?.toLowerCase());
    }
    else if (Array.isArray(f.filters)) {
      f.filters.forEach(inner => {
        if (inner.field === "Members") {
          memberFilters.push(inner.value?.toLowerCase());
        }
      });
    }
  });

  if (memberFilters.length) {
    temp = temp.filter(item =>
      item.MemberNamesArray?.some(name =>
        memberFilters.includes(name.toLowerCase())
      )
    );
  }

  const nonMemberFilters = (filter?.filters || []).filter(f =>
    f.field !== "Members" &&
    !(f.filters?.some(inner => inner.field === "Members"))
  );
  const processedFilter = { ...filter, filters: nonMemberFilters };

  if (processedFilter.filters.length) {
    temp = filterBy(temp, processedFilter);
  }

  let result = temp;
  if (Array.isArray(sort) && sort.length) {
    if (sort[0].field === "Members") {
      result = [...temp].sort((a, b) => {
        const aCount = a.MemberNamesArray?.length || 0;
        const bCount = b.MemberNamesArray?.length || 0;
        return sort[0].dir === "asc" ? aCount - bCount : bCount - aCount;
      });
    } else {
      result = orderBy(temp, sort);
    }
  }

  return result;
};

// Helper Components
export const ProjectIdCell = ({ dataItem, canEdit, isLive }) => (
  <td className="badge-edit-cell background-white k-table-td">
    <div className="badge-edit-content">
      <EnergyIdBadge text={dataItem.ProjectIdString} isLive={isLive} />
      {canEdit && dataItem && (
        <EditIconButton
          projectId={dataItem.ProjectId}
          projectName={dataItem.ProjectName}
        />
      )}
    </div>
  </td>
);

export const ProjectIdCellWithNewBadge = ({ dataItem, canEdit, isLive, isNewDeal }) => (
  <td className="badge-edit-cell background-white k-table-td">
    <div className="badge-edit-content" style={{ pointerEvents: 'none' }}>
      <EnergyIdBadge text={dataItem.ProjectIdString} isLive={isLive} />
      {
        isNewDeal && (
          <FluentBadge
            appearance="filled"
            size="medium"
            color="success"
            text="New"
            shape="rounded"
          />
        )
      }
      {
        canEdit && dataItem && (
          <EditIconButton
            projectId={dataItem.ProjectId}
            projectName={dataItem.ProjectName}
          />
        )
      }
    </div>
  </td>
);

export const ProjectIdCellWithNewBadgeEnergyDealResource = ({ dataItem, canEdit, isNewDeal, sectorNames }) => (
  <td className="badge-edit-cell background-white k-table-td">
    <div className="badge-edit-content" style={{ pointerEvents: 'none' }}>
      <FluentBadge
        shape="rounded"
        appearance="tint"
        size="medium"
        text={dataItem.ProjectIdString}
        className={getSectorColor(dataItem?.Sector, sectorNames)}
        color={getSectorColor(dataItem?.Sector, sectorNames)}
      />
      {
        isNewDeal && (
          <FluentBadge
            appearance="filled"
            size="medium"
            color="success"
            text="New"
            shape="rounded"
          />
        )
      }
      {
        dataItem && (
          <div style={{ pointerEvents: 'auto' }}>
            <EditIconButton
              projectId={dataItem.ProjectId}
              projectName={dataItem.ProjectName}
              disabled={!canEdit}
              tooltipContent={
                !canEdit ? (
                  <span>You are not a Member of this Deal therefore you cannot Edit it. Reach out to another Staffer or a Deal Member to get added to this Deal.</span>
                ) : undefined
              }
            />
          </div>
        )
      }
    </div>
  </td>
);
export const EditIconButton = ({ projectId, projectName, disabled = false, tooltipContent }) => {
  const [hovered, setHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const history = useHistory();

  const defaultTooltip = (
    <span>
      Edit <span style={{ fontWeight: 500 }}>{projectName}</span> Deal
    </span>
  );

  return (
    <div className={`edit-button-wrapper ${tooltipVisible ? "tooltip-open" : ""}`}>
      <Tooltip
        content={tooltipContent || defaultTooltip}
        relationship="label"
        positioning="above"
        onVisibleChange={(_, data) => setTooltipVisible(data.visible)}
      >
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{ display: "inline-block" }}
        >
          <FluentButton
            icon={
              <FluentUiIcon
                name={hovered && !disabled ? "EditFilled" : "EditRegular"}
                color={disabled ? "#D0D0D0" : "#0F6CBD"}
                size={20}
              />
            }
            onClick={() => {
              if (!disabled) {
                history.push({
                  pathname: `/projects/edit/${projectId}`,
                  state: { fromDashboard: true },
                });
              }
            }}
            appearance="transparent"
            className="edit-hover-button"
            disabled={disabled}
          />
        </div>
      </Tooltip>
    </div>
  );
};

export const EnergyBankerEmptyState = ({ canCreate }) => {
  const history = useHistory();

  return (
    <div className="empty-state-container">
      <img src={EmptyState} alt="No Data" />
      <p className="empty-state-header">
        Let's get you on a Deal!{" "}
        <FluentUiIcon name="Rocket24Filled" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        Any Deals you're a Member of will show up here. Start a{" "}
        {canCreate ? (
          <button
            onClick={() =>
              history.push({
                pathname: "/projects/create",
                state: { fromDashboard: true },
              })
            }
            className="create-deal-link"
          >
            new one
          </button>
        ) : (
          <span className="create-deal-link disabled" aria-disabled="true">new one</span>
        )}{" "}
        or ping your Staffer to join an existing Deal.
      </p>
    </div>
  );
};

export const EnergyBankerNoDealOnSearchState = ({ resetDataState, setShowGlobalSearchModal }) => {
  return (
    <div className="empty-state-container">
      <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
      <p className="empty-state-header">
        Hmm, nothing matched — but there are more places to look!{" "}
        <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        We couldn’t find any Deals you’re a Member of based on your search.<br /><br />
        You can{" "}
        <button
          type="button"
          onClick={resetDataState}
          className="create-deal-link"
        >
          reset your search
        </button>{" "} OR {" "}
        <button
          className="create-deal-link"
          onClick={() => setShowGlobalSearchModal(true)}
        >
          search Deals you’re not a Member of yet.
        </button><br />
        You can also try refining your keywords.
      </p>
    </div>
  );
};

export const EnergyBankerNoDealOnFilterState = ({ resetDataState }) => {
  return (
    <div className="empty-state-container  mt-4">
      <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
      <p className="empty-state-header">
        No results found with the current filters!{" "}
        <FluentUiIcon name="SearchRegular" color="#424242" size={20} />
      </p>
      <p className="empty-state-subtext">
        Try{" "}
        <button
          type="button"
          onClick={resetDataState}
          className="create-deal-link"
        >
          adjusting the filter options
        </button>{" "}
        to refine your results.
      </p>
    </div>
  );
};

export const SearchPromptMessage = ({ setShowGlobalSearchModal }) => {
  return (
    <div className="search-prompt-message">
      <span className="search-icon">
        <FluentUiIcon name="SearchRegular" size={14} />
      </span>
      <span>
        Looking for something outside your current Deals?{" "}
        <button
          className="search-link"
          onClick={() => setShowGlobalSearchModal(true)}
        >
          Try searching across all Deals.
        </button>
      </span>
    </div>
  );
};

export const EnergyStafferNoDealOnFilterState = ({ resetDataState }) => {
  return (
    <div className="empty-state-container mt-4">
      <img src={EmptyStateNoDealsOnFilter} alt="No Data" />
      <p className="empty-state-header">
        🕵️ Hmm, no records found — but don't give up just yet!
      </p>
      <p className="empty-state-subtext">
        Looks like there's nothing to show for the selected dates.
      </p>
      <p className="empty-state-subtext">
        You can try{" "}
        <button
          type="button"
          onClick={resetDataState}
          className="create-deal-link"
        >
          adjusting your filters
        </button>{" "}
        or pick a different date range to see updated results.
      </p>
    </div>
  );
};

export const EnergyBankerEmptyStateStaffer = ({ canCreate }) => {
  const history = useHistory();
  return (
    <div className="empty-state-container-staffer mt-4" data-testid="empty-state">
      <img src={EmptyStateStaffer} alt="No Data" />
      <p className="empty-state-header">
        No Active Deals on TRIYO yet.
      </p>
      <p className="empty-state-subtext">
        Any Deals created will show up here. Start a{" "}
        {canCreate ? (
          <button
            onClick={() =>
              history.push({
                pathname: "/projects/create",
                state: { fromDashboard: true },
              })
            }
            className="create-deal-link"
          >
            new one
          </button>
        ) : (
          <span className="create-deal-link disabled" aria-disabled="true">new one</span>
        )}
      </p>
    </div>
  );
};

export const EnergyAnalystsEmptyState = () => {
  return (
    <div className="empty-state-container--widget" data-testid="energy-analysts-empty-state">
      <img src={EmptyStateClipboard} alt="No Data" />
      <p className="empty-state-header">
        No Analysts or Associates onboarded yet
      </p>
      <p className="empty-state-subtext">
        There are currently no Energy Analysts or Associates onboarded to TRIYO. Once they are onboarded by RBC&apos;s AACG team, they will appear here.
      </p>
      <p className="empty-state-subtext">
        Need help in the meantime? TRIYO&apos;s Support Team is happy to assist.
      </p>
      <FluentButton
        appearance="secondaryWithOutline"
        text="Contact TRIYO Support"
        // TAD-5644 — `window.open(url, "_self")` is functionally equivalent
        // to `window.location.href = url` for mailto: URLs (both hand off
        // to the OS mail handler in the current tab) but `window.open` is
        // a regular method that's mockable via `vi.spyOn(window, "open")`.
        // `window.location` is non-configurable inside Vitest's `vmForks`
        // pool's vm.Context, so the assignment form can't be intercepted.
        onClick={() => window.open("mailto:support@triyo.io", "_self")}
      />
    </div>
  );
};

export const EnergyIdBadge = ({ text, isLive = true }) => {
  return (
    <FluentBadge
      shape="rounded"
      appearance="tint"
      size="medium"
      color={isLive ? "success" : "informative"}
      text={text}
    />
  );
};

EnergyIdBadge.propTypes = {
  text: PropTypes.string.isRequired,
  isLive: PropTypes.bool,
};

ProjectIdCell.propTypes = {
  dataItem: PropTypes.shape({
    ProjectId: PropTypes.number.isRequired,
    ProjectIdString: PropTypes.string.isRequired,
    ProjectName: PropTypes.string.isRequired,
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  isLive: PropTypes.bool,
};

EditIconButton.propTypes = {
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
};

EnergyBankerNoDealOnSearchState.propTypes = {
  resetDataState: PropTypes.func.isRequired,
  setShowGlobalSearchModal: PropTypes.func.isRequired,
};

EnergyBankerNoDealOnFilterState.propTypes = {
  resetDataState: PropTypes.func.isRequired,
};

EnergyBankerEmptyState.propTypes = {
  canCreate: PropTypes.bool.isRequired,
};

export const DealResourceEmptyState = ({ isMember, projectId }) => {
  const history = useHistory();
  
  const handleAddMembers = () => {
    history.push({
      pathname: `/projects/edit/${projectId}`,
      state: { fromDashboard: true },
    });
  };

  return (
    <div className="empty-state-container-staffer" data-testid="deal-resource-empty-state">
      <img src={EmptyStateClipboard} alt="No Data" />
      <p className="empty-state-header">
        No Analysts or Associates added to this Deal yet
      </p>
      <p className="empty-state-subtext">
        {isMember ? (
          <>
            There are currently no Analysts or Associates assigned to this Deal.
            <br />
            <br />
            Add Deal Team Members to begin managing Staffing and Resource allocation for this Deal.
          </>
        ) : (
          "There are currently no Analysts or Associates assigned to this Deal."
        )}
      </p>
      {isMember && (
        <FluentButton
          text="Add Deal Team Members"
          appearance="secondaryWithOutline"
          onClick={handleAddMembers}
        />
      )}
    </div>
  );
};

DealResourceEmptyState.propTypes = {
  isMember: PropTypes.bool.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

SearchPromptMessage.propTypes = {
  setShowGlobalSearchModal: PropTypes.func.isRequired,
};


const sectionColors = [
  "#E8EBFA", // light purple
  "#FDF5FC", // light pink
  "#EBF3FC", // light blue
  "#F1FAF1", // light green
  "#FDF6F3", // light orange
  "#FEFBF4", // light yellow
];



export const handleExportGroupedBySectorSingleSheet = async ({ data, columnsGrouped, emptySectors = [], priorityOrder = [] }) => {
  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Deals and Resource Log ${timestamp}.xlsx`;
  // Define a set of colors to cycle through
  const prioritizedData = (Array.isArray(data) ? data : []).sort((a, b) => {
    const aName = a.GroupName || "";
    const bName = b.GroupName || "";
    if (aName === bName) return 0;

    // Guard priorityOrder; treat missing as empty array
    const priorities = Array.isArray(priorityOrder) ? priorityOrder : [];
    const aIdx = priorities.indexOf(aName);
    const bIdx = priorities.indexOf(bName);
    const aIn = aIdx !== -1;
    const bIn = bIdx !== -1;

    if (aIn && !bIn) return -1;
    if (bIn && !aIn) return 1;
    if (aIn && bIn) return aIdx - bIdx;

    // Fallback alphabetical if neither is prioritized
    return aName.localeCompare(bName);
  });

  const columns = columnsGrouped.map(col => ({
    ...col,
    autoWidth: true
  }));
  // 1. Group data by sector
  const grouped = {};
  prioritizedData.forEach(item => {
    const sector = item.Sector || "Unknown";
    if (!grouped[sector]) grouped[sector] = [];
    grouped[sector].push(item);
  });
  const currentWeekHoursIndex = columns.findIndex(col => col.field === "CurrentWeekHours");

  // 2. Build rows: sector header row + data rows
  const rows = [
    {
      cells: columns.map(col => ({
        value: col.title,
        bold: true,
        background: "#D3D3D3" // Header row color
      }))
    }
  ];
  let colorIndex = 0;
  Object.entries(grouped).forEach(([sector, items]) => {
    const sectionColor = sectionColors[colorIndex % sectionColors.length];
    colorIndex++;
    // Sort items by GroupName: Live first, then Non-Live
    const sortedItems = [...items].sort((a, b) => {
      // First, sort by Live/Non-Live (Live comes first)
      if (a.Live !== b.Live) {
        if (a.Live === "Live") return -1;
        if (b.Live === "Live") return 1;
      }
      // Then, sort by Current Week Hours in descending order
      return (Number(b.CurrentWeekHours) || 0) - (Number(a.CurrentWeekHours) || 0);
    });
    // Demarcation row: sector name, rest empty, colored
    rows.push({
      cells: [
        { value: `Sector: ${sector}`, bold: true, background: sectionColor },
        ...columns.slice(1).map(() => ({ value: "", background: sectionColor }))
      ]
    });
    // Data rows
    sortedItems.forEach(row =>
      rows.push({
        cells: columns.map(col => ({
          value: row[col.field]
        }))
      })
    );
    // Subtotal row at the bottom of the section
    const sumCurrentWeekHours = items.reduce(
      (acc, row) => acc + (Number(row.CurrentWeekHours) || 0),
      0
    );
    rows.push({
      cells: columns.map((col, idx) =>
        idx === 0
          ? { value: "Subtotal", bold: true, background: sectionColor }
          : idx === currentWeekHoursIndex
            ? { value: sumCurrentWeekHours, bold: true, background: sectionColor }
            : { value: "", background: sectionColor }
      )
    });
  });

  //  Add empty sectors at the bottom
  const sectorsWithData = Object.keys(grouped);
  const sectorsWithoutData = emptySectors.filter(sector => !sectorsWithData.includes(sector));

  sectorsWithoutData.forEach((sector) => {
    const sectionColor = sectionColors[colorIndex % sectionColors.length];
    colorIndex++;

    // Demarcation row: sector name, rest empty, colored with lighter opacity
    rows.push({
      cells: [
        { value: `Sector: ${sector}`, bold: true, background: sectionColor },
        ...columns.slice(1).map(() => ({ value: "", background: sectionColor }))
      ]
    });

    // Subtotal row showing 0 hours
    rows.push({
      cells: columns.map((col, idx) =>
        idx === 0
          ? { value: "Subtotal", bold: true, background: sectionColor }
          : idx === currentWeekHoursIndex
            ? { value: 0, bold: true, background: sectionColor }
            : { value: "", background: sectionColor }
      )
    });
  });
  // 3. Build workbook
  const workbook = {
    sheets: [
      {
        title: "Deals and Resource Log",
        columns,
        rows,
        frozenRows: 1,
      }
    ]
  };

  // 4. Export as Excel
  const dataUrl = await toDataURL(workbook);

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleProjectHoursByVerticalExport = async (chartData) => {
  if (!chartData || chartData.length === 0) return;

  const timestamp = dayjs().format("YYYY-MM-DD HH-mm-ss");
  const fileName = `Project Hours by Vertical ${timestamp}.xlsx`;

  const sectorOrder = ["Upstream", "Midstream", "Richardson-Barr", "LNG", "Energy Transition", "Admin and One-Offs"];
   const sectorNamesInData = [
     ...new Set(chartData.flatMap((item) => (item.Sectors ?? []).map((s) => s.SectorName)))
   ];
    const sectors = [
    ...sectorOrder.filter((s) => sectorNamesInData.includes(s)),
    ...sectorNamesInData.filter((s) => !sectorOrder.includes(s)),
  ];

  const headerCells = ["Date", ...sectors].map((title) => ({ value: title, bold: true }));

  const dataRows = chartData.map((item) => ({
    cells: [
      { value: item.Date },
      ...sectors.map((sector) => ({
         value: (item.Sectors ?? []).find((s) => s.SectorName === sector)?.Hours ?? 0,      })),
    ],
  }));

  const workbook = {
    sheets: [
      {
        title: "Project Hours by Vertical",
        rows: [{ type: "header", cells: headerCells }, ...dataRows],
      },
    ],
  };

  const dataUrl = await toDataURL(workbook);
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};